import type { GraphEdge, GraphNode } from '../../api/settingsEaseedeskTicketTypeSheet'

/**
 * Desenul fluxului unui tip de tichet: un graf, nu o linie. Din orice status
 * pot pleca mai multi pasi (fork), iar pasii inapoi trec pe sub statusuri.
 */

export const CARD_W = 172
export const CARD_H = 108
/** Cea mai lata eticheta de pas; intre coloane ramane loc de ea si de 20px pe fiecare parte. */
export const BADGE_W = 96
const COL = CARD_W + BADGE_W + 40
const ROW = CARD_H + 56
const START_W = 78
const PAD = 16
const LANE = 46

export type LinkKind = 'start' | 'forward' | 'back' | 'side' | 'self'

export interface FlowCard {
  node: GraphNode
  x: number
  y: number
}

export interface FlowLink {
  edge: GraphEdge
  kind: LinkKind
  /** dintr-un status final intr-unul deschis: tichetul redevine activ */
  reopens: boolean
  path: string
  label: { x: number; y: number }
}

export interface FlowLayout {
  cards: FlowCard[]
  links: FlowLink[]
  start: { x: number; y: number } | null
  width: number
  height: number
}

export function kindLabel(kind: number): string {
  return ({ 1: 'open', 2: 'resolved', 3: 'closed' } as Record<number, string>)[kind] ?? 'other'
}

type P = { x: number; y: number }

/** Curba si punctul etichetei, la `t` din drum (mijlocul, de obicei). */
function cubic(a: P, b: P, c: P, d: P, t = 0.5) {
  const u = 1 - t
  const at = (k: 'x' | 'y') => u * u * u * a[k] + 3 * u * u * t * b[k] + 3 * u * t * t * c[k] + t * t * t * d[k]
  return { path: `M ${a.x} ${a.y} C ${b.x} ${b.y}, ${c.x} ${c.y}, ${d.x} ${d.y}`, label: { x: at('x'), y: at('y') } }
}

/** Curba care pleaca vertical din `a`, atinge banda `lane` la mijloc si intra vertical in `d`. */
function viaLane(a: P, d: P, lane: number) {
  const dip = (lane - (a.y + d.y) / 2) * (4 / 3)
  return cubic(a, { x: a.x, y: a.y + dip }, { x: d.x, y: d.y + dip }, d)
}

export function layoutFlow(nodes: GraphNode[], edges: GraphEdge[]): FlowLayout {
  if (nodes.length === 0) return { cards: [], links: [], start: null, width: 0, height: 0 }
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const columns = new Map<number, GraphNode[]>()
  for (const n of nodes) columns.set(n.column, [...(columns.get(n.column) ?? []), n])
  const maxCount = Math.max(...[...columns.values()].map((c) => c.length))

  // Ramurile se aseaza dupa randul celui din care pleaca, ca sa nu se incruciseze.
  const row = new Map<number, number>()
  const count = new Map<number, number>()
  const rel = new Map<number, P>()
  for (const col of [...columns.keys()].sort((a, b) => a - b)) {
    const parentRow = (n: GraphNode) => {
      const rows = edges.filter((e) => e.to === n.id && (byId.get(e.from)?.column ?? col) < col).map((e) => row.get(e.from) ?? 0)
      return rows.length ? Math.min(...rows) : Number.MAX_SAFE_INTEGER
    }
    const list = [...columns.get(col)!].sort((a, b) => parentRow(a) - parentRow(b) || a.id - b.id)
    const offset = ((maxCount - list.length) * ROW) / 2
    list.forEach((n, i) => {
      row.set(n.id, i)
      count.set(n.id, list.length)
      rel.set(n.id, { x: PAD + START_W + col * COL, y: offset + i * ROW })
    })
  }

  // Intoarcerile ocolesc statusurile: pe sus daca nimic nu sta deasupra capetelor, altfel pe jos.
  const above = (id: number) => (row.get(id) ?? 0) > 0
  const below = (id: number) => (row.get(id) ?? 0) < (count.get(id) ?? 1) - 1
  const backs = edges
    .filter((e) => e.from > 0 && e.from !== e.to && rel.has(e.from) && rel.has(e.to) && byId.get(e.to)!.column < byId.get(e.from)!.column)
    .sort((a, b) => byId.get(a.from)!.column - byId.get(a.to)!.column - (byId.get(b.from)!.column - byId.get(b.to)!.column) || a.id - b.id)
  const route = new Map<number, { top: boolean; lane: number }>()
  let topLanes = 0
  let bottomLanes = 0
  for (const e of backs) {
    const canTop = !above(e.from) && !above(e.to)
    const canBottom = !below(e.from) && !below(e.to)
    const top = canTop && (!canBottom || topLanes <= bottomLanes)
    route.set(e.id, { top, lane: top ? topLanes++ : bottomLanes++ })
  }

  const base = PAD + (topLanes ? 24 + topLanes * LANE : 34)
  const place = new Map<number, P>([...rel].map(([id, p]) => [id, { x: p.x, y: base + p.y }]))
  const bottom = Math.max(...[...place.values()].map((p) => p.y + CARD_H))
  const firstStart = edges.find((e) => e.from === 0 && place.has(e.to))
  const start = firstStart ? { x: PAD, y: place.get(firstStart.to)!.y + CARD_H / 2 } : null

  const links: FlowLink[] = []
  for (const edge of edges) {
    const to = place.get(edge.to)
    if (!to) continue
    if (edge.from === 0) {
      const a = { x: PAD + START_W - 16, y: start!.y }
      const d = { x: to.x, y: to.y + CARD_H / 2 }
      links.push({ edge, kind: 'start', reopens: false, ...cubic(a, { x: a.x + 8, y: a.y }, { x: d.x - 8, y: d.y }, d) })
      continue
    }
    const from = place.get(edge.from)
    if (!from) continue
    const reopens = byId.get(edge.from)!.kind !== 1 && byId.get(edge.to)!.kind === 1
    if (edge.from === edge.to) {
      const a = { x: from.x + CARD_W - 52, y: from.y }
      const d = { x: from.x + CARD_W - 16, y: from.y }
      links.push({ edge, kind: 'self', reopens, ...cubic(a, { x: a.x, y: a.y - 34 }, { x: d.x, y: d.y - 34 }, d) })
      continue
    }
    if (to.x > from.x) {
      const a = { x: from.x + CARD_W, y: from.y + CARD_H / 2 }
      const d = { x: to.x, y: to.y + CARD_H / 2 }
      const dx = (d.x - a.x) / 2
      links.push({ edge, kind: 'forward', reopens, ...cubic(a, { x: a.x + dx, y: a.y }, { x: d.x - dx, y: d.y }, d) })
      continue
    }
    if (to.x === from.x) {
      const a = { x: from.x + CARD_W, y: from.y + CARD_H / 2 + (to.y > from.y ? 14 : -14) }
      const d = { x: to.x + CARD_W, y: to.y + CARD_H / 2 + (to.y > from.y ? -14 : 14) }
      // Eticheta spre capatul de plecare: la mijloc ar sta peste pasii care ies spre coloana urmatoare.
      links.push({ edge, kind: 'side', reopens, ...cubic(a, { x: a.x + 120, y: a.y }, { x: d.x + 120, y: d.y }, d, 0.25) })
      continue
    }
    const r = route.get(edge.id)!
    const a = { x: from.x + CARD_W / 2 - 14, y: r.top ? from.y : from.y + CARD_H }
    const d = { x: to.x + CARD_W / 2 + 14, y: r.top ? to.y : to.y + CARD_H }
    const lane = r.top ? base - 24 - r.lane * LANE : bottom + 28 + r.lane * LANE
    links.push({ edge, kind: 'back', reopens, ...viaLane(a, d, lane) })
  }

  const maxCol = Math.max(...nodes.map((n) => n.column))
  return {
    cards: nodes.map((node) => ({ node, ...place.get(node.id)! })),
    links,
    start,
    width: PAD * 2 + START_W + maxCol * COL + CARD_W + 96,
    height: bottom + (bottomLanes ? 28 + (bottomLanes - 1) * LANE + 34 : 0) + PAD,
  }
}

export type ProblemKind = 'no_form' | 'empty_form' | 'dead_end' | 'unreachable'

export interface FlowProblem {
  status: number
  kind: ProblemKind
  severity: 'crit' | 'warn'
  title: string
  text: string
}

/** Ce ar opri un tichet pe fluxul asta, pe status. */
export function flowProblems(nodes: GraphNode[], edges: GraphEdge[], fields: (status: number) => number): FlowProblem[] {
  const problems: FlowProblem[] = []
  for (const n of nodes) {
    const entered = edges.some((e) => e.to === n.id && e.from > 0)
    if (entered && !n.form) {
      problems.push({ status: n.id, kind: 'no_form', severity: 'crit', title: 'No form', text: 'Save & next into this status fails: no form is set for it.' })
    } else if (n.form && fields(n.id) === 0) {
      problems.push({ status: n.id, kind: 'empty_form', severity: 'crit', title: 'Form has no fields', text: 'Save & next into this status fails with "Form settings not found".' })
    }
    if (n.kind === 1 && !edges.some((e) => e.from === n.id && e.to !== n.id)) {
      problems.push({ status: n.id, kind: 'dead_end', severity: 'crit', title: 'Open status with no way out', text: 'Tickets stop here and stay active. Add a step out, or make the status final.' })
    }
    if (n.unreachable) {
      problems.push({ status: n.id, kind: 'unreachable', severity: 'warn', title: 'No step leads here', text: 'No ticket can reach this status.' })
    }
  }
  return problems
}
